import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import { useRef, type ReactNode, type RefObject } from 'react'
import { cn } from '../lib/cn'
import { useIsPhone } from '../lib/useMediaQuery'
import { useSwipeDismiss } from '../lib/useSwipeDismiss'
import { LayerProvider, useLayer, Z } from './context'
import { IconButton } from './IconButton'

export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  /** 400 / 520 / 720 px; auf dem Telefon immer ein Sheet von unten */
  size?: 'sm' | 'md' | 'lg'
  /** feste Leiste unten: Abbrechen links, Hauptaktion rechts */
  footer?: ReactNode
  /** Esc und Klick daneben schließen; Standard true */
  dismissible?: boolean
  /** Element, das beim Öffnen den Fokus erhält (Standard: erstes fokussierbares Element) */
  initialFocus?: RefObject<HTMLElement | null>
  /** Inhalt ohne Innenabstand (Listen, Tabellen) */
  flush?: boolean
  children?: ReactNode
  'data-testid'?: string
}

const SIZE = { sm: 'md:max-w-100', md: 'md:max-w-130', lg: 'md:max-w-180' } as const

export const backdropClass =
  'fixed inset-0 bg-scrim transition-opacity duration-(--duration-base) ease-out data-ending-style:opacity-0 data-ending-style:duration-(--duration-fast) data-starting-style:opacity-0'

/** Dialog für Entscheidungen. Am Desktop zentriert, auf dem Telefon ein Sheet von unten. Bearbeitet wird im Drawer. */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  footer,
  dismissible = true,
  initialFocus,
  flush,
  children,
  'data-testid': testId,
}: ModalProps) {
  const zIndex = useLayer(Z.modal)
  // Telefon: Sheet von unten – am Griff und Kopf nach unten wischen schließt (wenn Esc es auch dürfte)
  const phone = useIsPhone()
  const popup = useRef<HTMLDivElement | null>(null)
  const swipe = useSwipeDismiss(popup, 'down', () => onOpenChange(false), { enabled: phone && dismissible })
  return (
    <BaseDialog.Root
      open={open}
      disablePointerDismissal={!dismissible}
      onOpenChange={(next, details) => {
        if (!next && !dismissible && details.reason === 'escape-key') return
        onOpenChange(next)
      }}
    >
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className={backdropClass} style={{ zIndex }} />
        {/* Rahmen = sichtbarer Bereich (Tastatur, base.css visible-frame) */}
        <BaseDialog.Viewport
          data-ui="dialog-frame"
          className="visible-frame flex items-end justify-center pt-[max(3rem,calc(env(safe-area-inset-top)+0.5rem))] md:items-center md:px-[max(1.5rem,env(safe-area-inset-left),env(safe-area-inset-right))] md:pt-[max(1.5rem,env(safe-area-inset-top))] md:pb-[max(1.5rem,env(safe-area-inset-bottom))]"
          style={{ zIndex }}
        >
          <BaseDialog.Popup
            ref={popup}
            initialFocus={initialFocus}
            data-testid={testId}
            data-ui="modal"
            className={cn(
              'flex max-h-full w-full flex-col rounded-t-lg border border-line bg-overlay text-fg shadow-lg outline-none md:rounded-lg',
              SIZE[size],
              'transition-[opacity,scale,translate] duration-(--duration-sheet) ease-out md:duration-(--duration-base)',
              'data-starting-style:translate-y-full md:data-starting-style:translate-y-1 md:data-starting-style:scale-98 md:data-starting-style:opacity-0',
              'data-ending-style:translate-y-full data-ending-style:duration-(--duration-base) data-ending-style:ease-in md:data-ending-style:translate-y-0 md:data-ending-style:opacity-0 md:data-ending-style:duration-(--duration-fast)',
            )}
          >
            <LayerProvider z={zIndex}>
              <div className={cn('flex shrink-0 flex-col', phone && dismissible && 'touch-none')} {...swipe}>
              <span className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-line-strong md:hidden" aria-hidden />
              <header className="flex shrink-0 items-start gap-3 px-5 pt-4 pb-3 md:pt-5">
                <div className="min-w-0 flex-1">
                  <BaseDialog.Title className="text-title text-fg">{title}</BaseDialog.Title>
                  {description && (
                    <BaseDialog.Description className="mt-1 text-body text-fg-muted">
                      {description}
                    </BaseDialog.Description>
                  )}
                </div>
                {dismissible && (
                  <BaseDialog.Close
                    render={
                      <IconButton
                        icon={X}
                        label="Schließen"
                        size="sm"
                        tooltip={false}
                        className="-mt-0.5 -mr-1.5 touch:size-11"
                      />
                    }
                  />
                )}
              </header>
              </div>
              {children !== undefined && children !== null && (
                <div
                  className={cn(
                    'min-h-0 flex-1 overflow-y-auto',
                    !flush && 'px-5 pb-5',
                    // Sheet ohne Aktionsleiste: der letzte Inhalt bleibt über dem Home-Indikator
                    !footer && 'max-md:pb-[max(1.25rem,env(safe-area-inset-bottom))]',
                  )}
                >
                  {children}
                </div>
              )}
              {footer && (
                <footer className="flex shrink-0 flex-col-reverse gap-2 border-t border-line bg-sunken/50 px-5 pt-3 pb-safe-bar md:flex-row md:items-center md:justify-end md:rounded-b-lg md:pb-3">
                  {footer}
                </footer>
              )}
            </LayerProvider>
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}
